"use client";

import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import type { DeviceDto } from "@/lib/api/types";
import { cn } from "@/lib/utils/cn";

import {
  DEVICE_TABLE_COLUMNS,
  type DeviceTableSortKey,
} from "@/modules/devices/components/device-table-columns";
import {
  DEVICE_TABLE_ROW_GRID,
  DeviceManagementTableRow,
} from "@/modules/devices/components/device-management-table-row";

export type { DeviceTableSortKey } from "@/modules/devices/components/device-table-columns";

const HEADER_H = 44;

type StickyScrollbarMetrics = {
  visible: boolean;
  left: number;
  width: number;
  scrollWidth: number;
};

const EMPTY_STICKY_SCROLLBAR: StickyScrollbarMetrics = {
  visible: false,
  left: 0,
  width: 0,
  scrollWidth: 0,
};

export type DeviceTableProps = {
  devices: DeviceDto[];
  selectedIds: ReadonlySet<string>;
  onToggleSelected: (id: string) => void;
  onTogglePageAll: (checked: boolean, pageDeviceIds: string[]) => void;
  onExternalDataAccess?: (d: DeviceDto) => void;
  onAssignUsers?: (d: DeviceDto) => void;
  onBackup?: (d: DeviceDto) => void;
  onBackupDelete?: (d: DeviceDto) => void;
  onDelete?: (d: DeviceDto) => void;
  onDisable?: (d: DeviceDto) => void;
  sort: DeviceTableSortKey | null;
  order: "asc" | "desc";
  onSortChange: (key: DeviceTableSortKey) => void;
};

function SortableHeader({
  label,
  active,
  asc,
  align = "left",
  onClick,
}: {
  label: string;
  sortKey: DeviceTableSortKey;
  active: boolean;
  asc: boolean;
  align?: "left" | "right" | "center";
  onClick: () => void;
}) {
  const Icon = active ? (asc ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Sort by ${label}`}
      aria-sort={active ? (asc ? "ascending" : "descending") : "none"}
      className={cn(
        "table-header-sort-btn group/sort -mx-1 flex w-[calc(100%+0.5rem)] min-w-0 items-center gap-1 rounded-lg px-1 py-1 transition-colors",
        align === "right" && "ml-auto justify-end",
        align === "center" && "mx-auto justify-center",
        align === "left" && "justify-start",
        active ? "text-foreground" : "text-muted hover:text-foreground",
      )}
    >
      <span className="truncate">{label}</span>
      <Icon
        className={cn(
          "h-3.5 w-3.5 shrink-0 transition-opacity",
          active ? "opacity-90 text-primary" : "opacity-40 group-hover/sort:opacity-70",
        )}
        aria-hidden
      />
    </button>
  );
}

export function DeviceTable({
  devices,
  selectedIds,
  onToggleSelected,
  onTogglePageAll,
  onExternalDataAccess,
  onAssignUsers,
  onBackup,
  onBackupDelete,
  onDelete,
  onDisable,
  sort,
  order,
  onSortChange,
}: DeviceTableProps) {
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const stickyScrollRef = useRef<HTMLDivElement | null>(null);
  const [stickyScrollbar, setStickyScrollbar] = useState<StickyScrollbarMetrics>(EMPTY_STICKY_SCROLLBAR);
  const pageIds = devices.map((d) => d.id);
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someSelected = selectedOnPage > 0 && !allSelected;
  const activeSort = sort ?? "last_seen";

  const updateStickyScrollbar = useCallback(() => {
    const table = tableScrollRef.current;
    if (!table) {
      return;
    }
    const rect = table.getBoundingClientRect();
    const next: StickyScrollbarMetrics = {
      visible:
        table.scrollWidth > table.clientWidth + 1 &&
        rect.top < window.innerHeight &&
        rect.bottom > 0,
      left: rect.left,
      width: rect.width,
      scrollWidth: table.scrollWidth,
    };
    setStickyScrollbar((current) => (
      current.visible === next.visible &&
      Math.round(current.left) === Math.round(next.left) &&
      Math.round(current.width) === Math.round(next.width) &&
      current.scrollWidth === next.scrollWidth
        ? current
        : next
    ));
  }, []);

  useLayoutEffect(() => {
    const table = tableScrollRef.current;
    if (!table) {
      return;
    }
    const update = () => updateStickyScrollbar();
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(table);
    if (table.firstElementChild) {
      observer?.observe(table.firstElementChild);
    }
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
      observer?.disconnect();
    };
  }, [updateStickyScrollbar]);

  const syncStickyScrollbar = useCallback(() => {
    const table = tableScrollRef.current;
    const sticky = stickyScrollRef.current;
    if (table && sticky && sticky.scrollLeft !== table.scrollLeft) {
      sticky.scrollLeft = table.scrollLeft;
    }
  }, []);

  const syncTableScrollbar = useCallback(() => {
    const table = tableScrollRef.current;
    const sticky = stickyScrollRef.current;
    if (table && sticky && table.scrollLeft !== sticky.scrollLeft) {
      table.scrollLeft = sticky.scrollLeft;
    }
  }, []);

  return (
    <>
      <div ref={tableScrollRef} onScroll={syncStickyScrollbar} className="table-shell scrollbar-hidden overflow-x-auto">
        <div className="w-max min-w-full" role="table">
        <div
          className={cn(
            DEVICE_TABLE_ROW_GRID,
            "table-grid-row table-header-row sticky top-0 z-[1] bg-[var(--table-header-bg)] text-micro font-semibold uppercase tracking-[0.08em] text-muted shadow-[inset_0_-1px_0_var(--table-border)]",
          )}
          style={{ minHeight: HEADER_H }}
          role="row"
        >
          {DEVICE_TABLE_COLUMNS.map((column) => {
            if (column.id === "select") {
              return (
                <div key={column.id} className="flex justify-center" role="columnheader">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary/20"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someSelected;
                      }
                    }}
                    onChange={(e) => onTogglePageAll(e.target.checked, pageIds)}
                    aria-label="Select all on this page"
                  />
                </div>
              );
            }

            if (column.id === "actions" || !column.sortKey) {
              return (
                <div
                  key={column.id}
                  className={cn(column.align === "right" && "text-right")}
                  role="columnheader"
                >
                  {column.label}
                </div>
              );
            }

            return (
              <div key={column.id} role="columnheader">
                <SortableHeader
                  label={column.label}
                  sortKey={column.sortKey}
                  active={activeSort === column.sortKey}
                  asc={order === "asc"}
                  align={column.align}
                  onClick={() => onSortChange(column.sortKey!)}
                />
              </div>
            );
          })}
        </div>

        <div role="rowgroup">
          {devices.map((d, index) => (
            <DeviceManagementTableRow
              key={d.id}
              device={d}
              rowIndex={index}
              selected={selectedIds.has(d.id)}
              onToggleSelected={onToggleSelected}
              onExternalDataAccess={onExternalDataAccess}
              onAssignUsers={onAssignUsers}
              onBackup={onBackup}
              onBackupDelete={onBackupDelete}
              onDelete={onDelete}
              onDisable={onDisable}
            />
          ))}
        </div>
        </div>
      </div>
      {stickyScrollbar.visible ? (
        <div
          ref={stickyScrollRef}
          onScroll={syncTableScrollbar}
          tabIndex={0}
          aria-label="Scroll data logger table horizontally"
          className="fixed bottom-0 z-50 h-5 overflow-x-scroll overflow-y-hidden rounded-t border border-card-border bg-card/95 shadow-overlay backdrop-blur"
          style={{ left: stickyScrollbar.left, width: stickyScrollbar.width }}
        >
          <div aria-hidden style={{ width: stickyScrollbar.scrollWidth, height: 1 }} />
        </div>
      ) : null}
    </>
  );
}
